import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthUser } from '@cloudbtl/shared';
import { api } from '../api/client.js';
import { isCustomTenantHost } from '../lib/auth-host.js';
import { clearReferralToken, loadReferralToken } from '../lib/visitor.js';

// 저장된 리퍼럴 토큰을 방금 로그인/가입한 계정에 귀속(best-effort, 1회 소진).
// 서버가 eventKey(attributed_signup:userId)·claimedAt 로 중복을 막으므로,
// 리퍼럴로 착지한 뒤 첫 인증 시점이 곧 그 계정의 귀속 순간이 된다.
function attributeReferralIfAny(): void {
  const token = loadReferralToken();
  if (!token) return;
  void api
    .attributeSignup(token)
    .catch(() => {})
    .finally(() => clearReferralToken());
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<AuthUser>;
  loginWithGoogle: (credential: string) => Promise<AuthUser>;
  signup: (email: string, password: string, name?: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const result = await api.me();
    setUser(result.user);
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      if (typeof window !== 'undefined' && isCustomTenantHost(window.location.hostname)) {
        const url = new URL(window.location.href);
        const handoff = url.searchParams.get('handoff');
        if (handoff) {
          await api.consumeAuthHandoff(handoff);
          url.searchParams.delete('handoff');
          window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        }
      }
      await refresh();
    };
    bootstrap()
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.login(email, password);
    setUser(result.user);
    return result.user;
  }, []);

  const loginWithGoogle = useCallback(async (credential: string) => {
    const result = await api.googleLogin(credential);
    setUser(result.user);
    // 구글은 최초 로그인이 곧 가입 — 리퍼럴 귀속 지점.
    attributeReferralIfAny();
    return result.user;
  }, []);

  const signup = useCallback(async (email: string, password: string, name?: string) => {
    const result = await api.signup(email, password, name);
    setUser(result.user);
    attributeReferralIfAny();
    return result.user;
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, refresh, login, loginWithGoogle, signup, logout }),
    [user, loading, refresh, login, loginWithGoogle, signup, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(Ctx);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
